import { useQuery, useQueryClient } from "@tanstack/react-query";

export interface MarketRewardsParams {
  /** USD pool distributed per day on this market. */
  ratePerDay: number;
  /** Min order size (shares) required to score. */
  minSize: number;
  /** Max spread from midpoint (in cents) for an order to score. */
  maxSpread: number;
}

/**
 * Batch variant — fetches reward params for many markets.
 *
 * Backend caps at 50 conditionIds per request (markets.ts:6597 rejects 400
 * on >50). Events with >50 child markets would render no rewards badge
 * otherwise. Chunk client-side and merge.
 *
 * Returns a Record keyed by conditionId (lowercase — Polymarket APIs return
 * lowercased hex). Consumer should also lowercase when looking up.
 */
const MARKETS_REWARDS_BATCH_SIZE = 50;

export function useMarketsRewardsParams(
  conditionIds: string[] | undefined,
  enabled: boolean = true,
) {
  const ids = (conditionIds || []).filter((x) => typeof x === "string" && x.length > 0);
  const key = [...ids].sort().join(",");
  return useQuery<Record<string, MarketRewardsParams>>({
    queryKey: ["markets-rewards-params", key],
    queryFn: async () => {
      if (ids.length === 0) return {};
      const chunks: string[][] = [];
      for (let i = 0; i < ids.length; i += MARKETS_REWARDS_BATCH_SIZE) {
        chunks.push(ids.slice(i, i + MARKETS_REWARDS_BATCH_SIZE));
      }
      // Throw on chunk failure so React Query retries the whole query with
      // exponential backoff. Previously this returned {} silently, which
      // cached the PARTIAL union as if it were the complete answer and
      // the missing chunk never recovered until staleTime lapsed.
      // Route to /api/polymarket/rewards which hits CLOB directly per
      // conditionId with ?sponsored=true (mirrors polymarket.com). The older
      // /api/markets/rewards goes through our backend cache and was missing
      // most sponsored pools, leaving most rows without a gift icon.
      const results = await Promise.all(
        chunks.map(async (chunk) => {
          const r = await fetch(`/api/polymarket/rewards?conditionIds=${chunk.join(",")}`);
          if (!r.ok) throw new Error(`markets-rewards chunk ${r.status}`);
          const j = await r.json();
          return (j?.rewards || {}) as Record<string, any>;
        }),
      );
      const out: Record<string, MarketRewardsParams> = {};
      for (const rewards of results) {
        for (const [cid, v] of Object.entries(rewards)) {
          out[cid] = {
            ratePerDay: Number(v?.ratePerDay || 0),
            minSize: Number(v?.minSize || 0),
            maxSpread: Number(v?.maxSpread || 0),
          };
        }
      }
      return out;
    },
    enabled: enabled && ids.length > 0,
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * Fetches live Polymarket rewards params for a single market.
 *
 * Mounted on every EventCard, so on the markets list (~20 cards) it
 * was firing 20 separate /api/polymarket/rewards calls. The parent
 * page also calls useMarketsRewardsParams (the batch variant) for
 * the same set, so the 20 individuals were pure waste.
 *
 * The fix: before firing the individual request, peek at any cached
 * `["markets-rewards-params", ...]` batch results and use the row
 * from there if it covers this conditionId. React Query's default
 * staleTime=60min makes the batch result a near-permanent cache hit
 * during a single browse session.
 */
export function useMarketRewardsParams(conditionId: string | undefined, enabled: boolean = true) {
  const queryClient = useQueryClient();
  return useQuery<MarketRewardsParams | null>({
    queryKey: ["market-rewards-params", conditionId],
    queryFn: async () => {
      if (!conditionId) return null;
      const lc = conditionId.toLowerCase();

      // Inspect every batch ['markets-rewards-params', …] query. The batch
      // queryKey's second segment is the sorted+joined list of conditionIds
      // that were requested, so we can tell:
      //   - hit  : batch resolved AND included our cid in its returned data
      //   - miss : batch resolved AND included our cid in its INPUT but
      //            Polymarket returned no rewards row → cache as null
      //            (markets without a sponsored pool are simply omitted
      //            from the response)
      //   - n/a  : our cid wasn't in the batch's input set → fall through
      //            to the individual fetch (event-detail page, OrderBook)
      type Lookup =
        | { kind: "hit"; data: MarketRewardsParams }
        | { kind: "miss" }
        | { kind: "absent"; anyBatchExists: boolean };
      const lookup = (): Lookup => {
        const queries = queryClient.getQueryCache().findAll({
          queryKey: ["markets-rewards-params"],
        });
        let anyBatchExists = false;
        for (const q of queries) {
          anyBatchExists = true;
          const data = q.state.data as Record<string, MarketRewardsParams> | undefined;
          if (data) {
            const row = data[lc] || data[conditionId];
            if (row) return { kind: "hit", data: row };
          }
          // Did the batch's INPUT include this cid? queryKey[1] is the
          // sorted+joined ids string. Substring check is safe — every id
          // is the full 0x-prefixed 64-char hex, no overlap risk.
          const key = q.queryKey[1];
          if (typeof key === "string" && (key.includes(lc) || key.includes(conditionId))) {
            // Resolved batch + input matched + no row in data = no rewards.
            if (q.state.status === "success") return { kind: "miss" };
          }
        }
        return { kind: "absent", anyBatchExists };
      };

      const first = lookup();
      if (first.kind === "hit") return first.data;
      if (first.kind === "miss") return null;

      // First-mount race: the parent page's batch query fires in parallel
      // with every EventCard's individual query. Without this gate we'd
      // fire 20-30 single-id /api/polymarket/rewards requests on top of
      // the batch. Poll until any in-flight batch settles instead of a
      // fixed 800 ms — the backend hits CLOB serially per cid and can
      // take 1-2 s, so the old fixed timeout was timing out before the
      // batch had a chance to claim our cid (10 redundant individuals
      // observed in Chrome DevTools traces over /markets/trending).
      if (first.anyBatchExists) {
        const POLL_INTERVAL_MS = 100;
        // The backend hits CLOB serially per cid with no edge cache
        // (rewards responses are `private, no-store`), so a 16-cid batch
        // can take >3 s end to end. Measured on /markets/trending: the
        // batch returned ~4 s after mount, the old 3 s cap expired first,
        // and all 16 EventCards fell through to redundant single-id
        // fetches. 10 s comfortably covers the batch while still bailing
        // early via the `stillFetching` check once the batch settles.
        const MAX_WAIT_MS = 10000;
        const startedAt = Date.now();
        while (Date.now() - startedAt < MAX_WAIT_MS) {
          await new Promise<void>((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
          const tick = lookup();
          if (tick.kind === "hit") return tick.data;
          if (tick.kind === "miss") return null;
          // Stop early once no batch is fetching anymore — there's
          // nothing left to wait on, fall through to the individual.
          const stillFetching = queryClient.getQueryCache().findAll({
            queryKey: ["markets-rewards-params"],
          }).some((q) => q.state.fetchStatus === "fetching");
          if (!stillFetching) break;
        }
      }

      const r = await fetch(`/api/polymarket/rewards?conditionIds=${conditionId}`);
      if (!r.ok) return null;
      const j = await r.json();
      const row = j?.rewards?.[lc] || j?.rewards?.[conditionId];
      if (!row) return null;
      return {
        ratePerDay: Number(row.ratePerDay || 0),
        minSize: Number(row.minSize || 0),
        maxSpread: Number(row.maxSpread || 0),
      };
    },
    enabled: enabled && !!conditionId,
    staleTime: 60 * 60 * 1000,
  });
}
